import { useHistory } from 'react-router-dom';
import classnames from 'classnames';

import { ReactComponent as PolysomnographySVG } from 'icons/pg-psg.svg';
import { ReactComponent as CheckCircleSVG } from 'icons/table/check-circle.svg';
import { ReactComponent as ClockSVG } from 'icons/table/clock.svg';

import { getHintFromPaymentStatus } from 'helpers/hint';
import useWindowDimensions, {
  isMobileResolution,
} from 'helpers/useWindowDimensions';

import { STATUSES } from 'config/polygraphy';
import { compareDesc } from 'date-fns';
import { timeInParis } from 'helpers/timezone';

const PolysomnographyCardNew = ({ data, date, userType }) => {
  const history = useHistory();
  const { width } = useWindowDimensions();
  const isMobile = isMobileResolution(width);

  const { id, doctor, technician, deliveryDate, status, paymentIntents } = data;

  const patientPaymentIntent = paymentIntents?.filter(
    ({ type }) => type === 'patient',
  );
  const succeededPayment = patientPaymentIntent?.some(
    ({ status }) => status === 'succeeded',
  );
  const intent = succeededPayment
    ? patientPaymentIntent
        .filter(({ status }) => status === 'succeeded')
        .sort((a, b) =>
          compareDesc(timeInParis(a.createdAt), timeInParis(b.createdAt)),
        )
    : patientPaymentIntent?.sort((a, b) =>
        compareDesc(timeInParis(a.createdAt), timeInParis(b.createdAt)),
      );

  const getStatusAttributes = (status) => {
    const redBadge = [
      'late-cancellation-by-patient',
      'cancelled-by-host',
      'timely-cancellation-by-patient',
    ].includes(status);

    const wrapperClasses = classnames('th-badge mr5', {
      'th-badge-gray': ['to-be-scheduled', 'patient-doesnt-want'].includes(
        status,
      ),
      'th-badge-yellow': status === 'scheduled',
      'th-badge-red-new': redBadge || status === 'no-show',
      'th-badge-green': ['conducted', 'succeeded'].includes(status),
    });

    const paymentClasses = classnames('th-badge mr5', {
      'th-badge-green': succeededPayment,
      'th-badge-grey': !!succeededPayment,
    });

    const articleClasses = classnames('grid-cards', {
      pointer: userType !== 'non-sleep',
    });

    return {
      wrapperClasses,
      paymentClasses,
      articleClasses,
      statusText: redBadge ? 'canceled' : status,
    };
  };

  const icon = succeededPayment ? <CheckCircleSVG /> : <ClockSVG />;

  const showPaymentBadge =
    userType !== 'non-sleep' && paymentIntents?.length > 0;

  const { wrapperClasses, paymentClasses, articleClasses, statusText } =
    getStatusAttributes(status);

  const DesktopView = (
    <article
      className={articleClasses}
      onClick={() =>
        userType === 'non-sleep'
          ? null
          : history.push(`/polysomnographies/${id}`)
      }
    >
      <div className="apps-card-left-new" style={{ gridColumn: 'span 6' }}>
        <div className="mr6">
          <PolysomnographySVG />
        </div>
        <div className="apps-card-meta">
          <div className="apps-card-app-title-new">Polysomnography</div>
          <div className="flex flex-row" style={{ gap: '6px' }}>
            <div>
              {technician && (
                <p className="apps-card-app-desc-new">Technician:</p>
              )}
              {doctor && <p className="apps-card-app-desc-new">Doctor:</p>}
            </div>
            <div className="flex flex-column">
              {technician && (
                <p className="apps-card-app-desc-new">
                  {technician?.firstName} {technician?.lastName}
                </p>
              )}
              {doctor && (
                <p className="apps-card-app-desc-new">
                  {doctor?.lastName?.toUpperCase()} {doctor?.firstName}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="apps-card-right-new" style={{ gridColumn: 'span 2' }}>
        {statusText && (
          <span className={wrapperClasses}>
            {statusText === 'canceled'
              ? statusText
              : STATUSES.find((s) => s.value === statusText).label}
          </span>
        )}
      </div>
      <div className="apps-card-right-new" style={{ gridColumn: 'span 2' }}>
        {showPaymentBadge ? (
          <span
            className={paymentClasses}
            data-rh={
              getHintFromPaymentStatus[
                succeededPayment ? 'succeeded' : 'incomplete'
              ]
            }
          >
            {' '}
            {icon}€{intent[0]?.amount}
          </span>
        ) : null}
      </div>
      <div
        className="apps-card-right-new"
        style={{ gridColumn: 'span 2', justifySelf: 'end' }}
      >
        {deliveryDate && (
          <div>
            <p className="apps-card-app-desc-new">Delivery date:</p>
            <p className="apps-card-app-desc-new">{date}</p>
          </div>
        )}
      </div>
    </article>
  );

  const MobileView = (
    <article
      className={articleClasses}
      onClick={() =>
        userType === 'non-sleep'
          ? null
          : history.push(`/polysomnographies/${id}`)
      }
    >
      <div className="apps-card-left-new" style={{ gridColumn: 'span 10' }}>
        <div className="apps-card-meta">
          <div className="apps-card-app-title-new">Polysomnography</div>
          <div className="flex flex-row" style={{ gap: '6px' }}>
            <div>
              {technician && (
                <p className="apps-card-app-desc-new">Technician:</p>
              )}
              {doctor && <p className="apps-card-app-desc-new">Doctor:</p>}
            </div>
            <div className="flex flex-column">
              {technician && (
                <p className="apps-card-app-desc-new">
                  {technician?.firstName} {technician?.lastName}
                </p>
              )}
              {doctor && (
                <p className="apps-card-app-desc-new">
                  {doctor?.lastName?.toUpperCase()} {doctor?.firstName}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className="apps-card-right-new"
        style={{ gridColumn: 'span 2', justifySelf: 'end' }}
      >
        {deliveryDate && (
          <div>
            <p className="apps-card-app-desc-new">Delivery date:</p>
            <p className="apps-card-app-desc-new">{date}</p>
          </div>
        )}
      </div>
      <div
        className="apps-card-right-new"
        style={{ gridColumn: 'span 6', gap: '8px' }}
      >
        {statusText && (
          <span className={wrapperClasses}>
            {statusText === 'canceled'
              ? statusText
              : STATUSES.find((s) => s.value === statusText).label}
          </span>
        )}
        {showPaymentBadge ? (
          <span
            className={paymentClasses}
            data-rh={
              getHintFromPaymentStatus[
                succeededPayment ? 'succeeded' : 'incomplete'
              ]
            }
          >
            {' '}
            {icon}€{intent[0]?.amount}
          </span>
        ) : null}
      </div>
    </article>
  );

  return isMobile ? MobileView : DesktopView;
};
export default PolysomnographyCardNew;
